import { ClassData, JackRabbitClassesDataResponse } from "../dtos/classes.type";
import { URL_PARAMETERS } from "../utils/const.util";
import {
  filterByAge,
  filterByDays,
  filterByFullClasses,
  filterByGender,
  filterByKeyAndValue,
  filterByLocation,
  filterByMultipleValues,
} from "../utils/filters.util";
import { getAllParamsFromURL } from "../utils/url.util";
import { trackingService } from "./tracking.service";

/**
 * A singleton that stores the list of data fetched from the server.
 *
 * Provides simple filtering options for the data;
 *
 */
class RYBConnectClasses {
  public static instance: RYBConnectClasses;
  private isLoading: boolean = false;
  private data: null | ClassData[] = null;
  private loadPromise: null | Promise<ClassData[]> = null;
  private CATEGORY_KEY = ryb_connect_classes_data.filters.wizard_category_key;
  private CATEGORY_VALUE =
    ryb_connect_classes_data.filters.wizard_category_value;
  private IS_FILTERED_LIST = true;

  constructor() {
    if (RYBConnectClasses.instance) {
      return RYBConnectClasses.instance;
    }
    trackingService.init();
    this.IS_FILTERED_LIST =
      Boolean(ryb_connect_classes_data.filters.wizard_category_key) &&
      ryb_connect_classes_data.filters.wizard_category_key !== "null";
    RYBConnectClasses.instance = this;
  }

  /**
   * Get's unfiltered data from the API or from the instance
   * @returns {data}
   */
  async getData() {
    if (this.data) {
      return this.data;
    }

    if (this.isLoading) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = new Promise(async (resolve, reject) => {
      try {
        // @note :
        // this is not really pretty - think about of better way to optimize
        // dynamically the combination between category and category value
        const hasCategoryOne =
          this.CATEGORY_VALUE &&
          this.CATEGORY_VALUE.toLocaleLowerCase() !== "all" &&
          this.CATEGORY_VALUE.toLocaleLowerCase() !== "null";

        const apiUrl = hasCategoryOne
          ? `${ryb_connect_connection_details.url}&${this.CATEGORY_KEY}=${this.CATEGORY_VALUE}`
          : ryb_connect_connection_details.url;
        const response = await fetch(apiUrl, {
          method: "GET",
        });

        const data: JackRabbitClassesDataResponse = await response.json();
        if (data.success) {
          this.data =
            this.IS_FILTERED_LIST &&
            this.CATEGORY_VALUE !== "all" &&
            this.CATEGORY_VALUE !== "null"
              ? data.rows.filter(
                  (item) => item.category1 === this.CATEGORY_VALUE
                )
              : data.rows;

          this.data = [...this.data].sort((a, b) => {
            const timeA = new Date(`1970-01-01T${a.start_time}:00`);
            const timeB = new Date(`1970-01-01T${b.start_time}:00`);
            return timeA.getTime() - timeB.getTime();
          });
          this.isLoading = false;
          resolve(this.data);
        } else {
          trackingService.captureException(
            new Error(
              `${window.location.host} | Jackrabbit respond with error in ${window.location.pathname}`
            ),
            {
              requestedUrl: ryb_connect_connection_details.url,
              devMessage: "Jackrabbit returned error",
              response: data,
            }
          );
          this.isLoading = false;
          reject("Error: " + data);
        }
      } catch (error) {
        trackingService.captureException(
          new Error(
            `${window.location.host} | Exception - Jackrabbit respond with error in ${window.location.pathname}`
          ),
          {
            requestedUrl: ryb_connect_connection_details.url,
            devMessage: "Jackrabbit returned error",
            error: error,
          }
        );
        this.isLoading = false;
        reject("Error: Unable to fetch class openings.");
      }
    });

    return this.loadPromise;
  }

  /**
   * Get's filtered by parameters data
   *
   * @note This needs refactoring in order to make it more versatile. Currently it supports only our initial version.
   *
   * @returns {data}
   */
  async getFilteredDataByURLParams(): Promise<ClassData[]> {
    const filters = getAllParamsFromURL();
    if (!filters) {
      return this.data || [];
    }
    let data = structuredClone(this.data) || [];

    // Delete not needed filters:
    if (filters[URL_PARAMETERS.STEP]) {
      delete filters[URL_PARAMETERS.STEP];
      delete filters[URL_PARAMETERS.USER];
      delete filters[URL_PARAMETERS.CONTACT];
    }

    for (const [key, value] of Object.entries(filters)) {
      if (key === URL_PARAMETERS.AGE) {
        data = filterByAge(data);
      } else if (key === URL_PARAMETERS.FULL_CLASSES) {
        data = filterByFullClasses(data);
      } else if (key === URL_PARAMETERS.CATEGORY) {
        data = filterByMultipleValues(data, filters[URL_PARAMETERS.CATEGORY]);
      } else if (!ryb_connect_classes_data.wizard.steps["2"].length) {
        if (filters[URL_PARAMETERS.LOCATION]) {
          data = filterByLocation(data);
        }
      } else {
        data = filterByKeyAndValue(data, key, value);
      }
    }

    return data;
  }

  /**
   * used for finder because of odd product requirements
   */
  async getFilteredDataByAgeAndGenderURLParams(): Promise<ClassData[]> {
    const filters = getAllParamsFromURL();
    if (!filters) {
      return this.data || [];
    }
    let data = structuredClone(this.data) || [];
    if (filters[URL_PARAMETERS.GENDER]) {
      data = filterByGender(data);
    }

    if (filters[URL_PARAMETERS.AGE]) {
      data = filterByAge(data);
    }

    if (filters[URL_PARAMETERS.FULL_CLASSES]) {
      data = filterByFullClasses(data);
    }

    if (filters[URL_PARAMETERS.DAYS]) {
      data = filterByDays(data, filters[URL_PARAMETERS.DAYS]);
    }
    return data;
  }
}

export default RYBConnectClasses;
